import React, { useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import { useForm, Controller } from "react-hook-form";
import { MdDelete } from "react-icons/md";
import { FaPlus, FaCopy, FaCodeBranch } from "react-icons/fa";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import Select from "react-select";
import { adjustToNoon } from "helpers/utils";
import menuPortalProps from "helpers/reactSelectPortal";
import { getChangeOrderPhases } from "redux/crm/project/changeOrderSlice";
import { Accordion, Button, Modal, Alert, FormRow } from 'components/common';

// ── Shared control classes ────────────────────────────────────────────────────
import { INPUT_CLS as inputCls } from 'helpers/formStyles';
const thCls =
  "tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-semibold tw-text-slate-600 tw-whitespace-nowrap";
const tdCls = "tw-px-3 tw-py-2 tw-align-top";

const FieldError = ({ error }) =>
  error ? (
    <p className="tw-text-xs tw-text-red-500 tw-mt-0.5 tw-mb-0">
      {error.message}
    </p>
  ) : null;

FieldError.propTypes = { error: PropTypes.object };

// ── Add-record link ───────────────────────────────────────────────────────────
const AddRecordButton = ({ onClick }) => (
  <div className="tw-flex tw-justify-end tw-items-center tw-mt-3">
    <button
      type="button"
      onClick={onClick}
      className="tw-inline-flex tw-items-center tw-gap-2 tw-bg-transparent tw-border-0 tw-cursor-pointer tw-text-sm tw-text-primary-600 hover:tw-text-primary-700 tw-transition-colors"
    >
      <span className="tw-inline-flex tw-items-center tw-justify-center tw-w-6 tw-h-6 tw-rounded-xl tw-border tw-border-dashed tw-border-primary-400">
        <FaPlus size={10} />
      </span>
      Add Record
    </button>
  </div>
);

AddRecordButton.propTypes = { onClick: PropTypes.func.isRequired };

const AccordionHeader = ({ children }) => (
  <span className="tw-font-medium tw-text-slate-900">{children}</span>
);

AccordionHeader.propTypes = { children: PropTypes.node };

/**
 * Reusable date cell used across the Estimate / APD / Shop / Other sections.
 * Declared at module scope (not inside RequestScheduleForm) so its component
 * type is stable across renders — otherwise every keystroke would remount the
 * DatePicker and drop focus.
 *
 * `onChanged` receives (adjustedDate, rawDate); a few call sites intentionally
 * persist the raw date.
 */
const DateCell = ({ control, error, name, rules, onChanged, disabled }) => (
  <>
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field }) => (
        <DatePicker
          selected={field.value}
          onChange={date => {
            const adjustedDate = adjustToNoon(date);
            field.onChange(adjustedDate);
            onChanged(adjustedDate, date);
          }}
          className={inputCls}
          wrapperClassName="tw-w-full"
          placeholderText="dd-MMM-YYYY"
          dateFormat="dd-MMM-yyyy"
          fixedHeight
          disabled={disabled}
        />
      )}
    />
    <FieldError error={error} />
  </>
);

DateCell.propTypes = {
  control: PropTypes.object.isRequired,
  error: PropTypes.object,
  name: PropTypes.string.isRequired,
  rules: PropTypes.object,
  onChanged: PropTypes.func.isRequired,
  disabled: PropTypes.bool
};

const RequestScheduleForm = ({
  changeOrder,
  estimateData,
  setEstimateData,
  abErectionData,
  setAbErectionData,
  apdData,
  setApdData,
  shopData,
  setShopData,
  otherData,
  setOtherData,
  isScheduled
}) => {
  const {
    control,
    register,
    formState: { errors },
    setValue
  } = useForm();
  const [activeKey, setActiveKey] = useState(
    Array.from({ length: 5 }, (_, index) => index.toString())
  );

  const dispatch = useDispatch();
  const { project, building } = changeOrder || {};

  // ── Copy Shop / Erection drawings from another CO# (client-side) ──────────────
  const [showCopyShopModal, setShowCopyShopModal] = useState(false);
  const [copyShopSourceId, setCopyShopSourceId] = useState("");
  const [copyingShop, setCopyingShop] = useState(false);

  // Other CO#s of this building we can copy phases from (exclude the current one).
  const copyShopSourceOptions = useMemo(
    () =>
      (changeOrder?.list_change_orders || [])
        .filter(co => co.id !== changeOrder?.id)
        .map(co => ({ value: String(co.id), label: `CO#${co.co}` })),
    [changeOrder]
  );

  const handleCopyShop = async () => {
    if (!copyShopSourceId) return;
    setCopyingShop(true);
    try {
      const phases = await dispatch(
        getChangeOrderPhases({
          projectId: project.id,
          buildingId: building.id,
          changeOrderId: copyShopSourceId
        })
      ).unwrap();

      // Skip phases whose name already exists in the form (matches the backend's
      // find_by_name upsert, which would merge duplicates on submit anyway).
      const existingNames = new Set(
        (shopData || []).map(item => (item.name || "").trim())
      );
      const newRows = (phases || [])
        .filter(p => !existingNames.has((p.name || "").trim()))
        .map((p, index) => ({
          id: `copied_${copyShopSourceId}_${index}`,
          name: p.name,
          description: p.description,
          shop_requested: p.shop_requested,
          erc_requested: p.erc_requested
        }));

      if (newRows.length === 0) {
        toast.info("No new shop drawings to copy (all names already exist).");
      } else {
        setShopData(prevState => [...prevState, ...newRows]);
        toast.success(`${newRows.length} shop drawing(s) copied.`);
      }

      setShowCopyShopModal(false);
      setCopyShopSourceId("");
    } catch (error) {
      toast.error(error?.error || error?.message || "Failed to copy shop drawings.");
    } finally {
      setCopyingShop(false);
    }
  };

  useEffect(() => {
    if (estimateData) {
      const requested_date =
        estimateData.est_requested_date || estimateData.requested_date;
      setValue(
        "est_requested_date",
        requested_date ? new Date(requested_date) : null
      );

      if (isScheduled) {
        const scheduled_date =
          estimateData.est_scheduled_date || estimateData.scheduled_date;
        setValue(
          "est_scheduled_date",
          scheduled_date ? new Date(scheduled_date) : null
        );
      }
    }

    if (abErectionData) {
      const requested_date = abErectionData.requested_date;
      setValue(
        "requested_date",
        requested_date ? new Date(requested_date) : null
      );

      if (isScheduled) {
        const scheduled_date = abErectionData.scheduled_date;
        setValue(
          "scheduled_date",
          scheduled_date ? new Date(scheduled_date) : null
        );
      }
    }

    if (apdData) {
      apdData.forEach(item => {
        setValue(`apd_name_${item.id}`, item.name);
        setValue(
          `apd_requested_${item.id}`,
          item.apd_requested ? new Date(item.apd_requested) : null
        );
        setValue(
          `ab_erd_requested_${item.id}`,
          item.ab_erd_requested ? new Date(item.ab_erd_requested) : null
        );

        if (isScheduled) {
          setValue(
            `apd_scheduled_${item.id}`,
            item.apd_scheduled ? new Date(item.apd_scheduled) : null
          );
          setValue(
            `ab_erd_scheduled_${item.id}`,
            item.ab_erd_scheduled ? new Date(item.ab_erd_scheduled) : null
          );
        }
      });
    }

    if (shopData) {
      shopData.forEach(item => {
        setValue(`shop_name_${item.id}`, item.name);
        setValue(`description_${item.id}`, item.description);
        setValue(
          `shop_requested_${item.id}`,
          item.shop_requested ? new Date(item.shop_requested) : null
        );
        setValue(
          `erc_requested_${item.id}`,
          item.erc_requested ? new Date(item.erc_requested) : null
        );

        if (isScheduled) {
          setValue(
            `shop_scheduled_${item.id}`,
            item.shop_scheduled ? new Date(item.shop_scheduled) : null
          );
          setValue(
            `erc_scheduled_${item.id}`,
            item.erc_scheduled ? new Date(item.erc_scheduled) : null
          );
        }
      });
    }

    if (otherData) {
      otherData.forEach(item => {
        setValue(`other_name_${item.id}`, item.name);
        setValue(
          `requested_date_${item.id}`,
          item.requested_date ? new Date(item.requested_date) : null
        );

        if (isScheduled) {
          setValue(
            `scheduled_date_${item.id}`,
            item.scheduled_date ? new Date(item.scheduled_date) : null
          );
        }
      });
    }
  }, [setValue, estimateData, abErectionData, apdData, shopData, otherData, isScheduled]);

  const handleAccordionSelect = newActiveKey => {
    setActiveKey(newActiveKey);
  };

  const handleAddAPD = () => {
    setApdData(prevState => [
      ...prevState,
      {
        id: prevState.length + 1,
        apd_requested: new Date()
        //ab_requested: new Date()
      }
    ]);
  };

  const handleAddShop = () => {
    setShopData(prevState => [
      ...prevState,
      {
        id: prevState.length + 1,
        shop_requested: new Date()
        //erc_requested: new Date()
      }
    ]);
  };

  const handleAddOther = () => {
    setOtherData(prevState => [
      ...prevState,
      {
        id: prevState.length + 1,
        requested_date: new Date()
      }
    ]);
  };

  const deleteItem = (data, setData, id) => {
    const newData = data.filter(item => item.id !== id);
    setData(newData);
  };

  const handleDeleteApd = id => {
    deleteItem(apdData, setApdData, id);
  };

  const handleDeleteShop = id => {
    deleteItem(shopData, setShopData, id);
  };

  const handleDeleteOther = id => {
    deleteItem(otherData, setOtherData, id);
  };

  const handleInputChange = (setData, name, id, value, fieldKey) => {
    setData(prevData => {
      const updateData = prevData.map(item =>
        item.id === id ? { ...item, [name]: value } : item
      );
      return updateData;
    });

    setValue(fieldKey || `${name}_${id}`, value);
  };

  const handleUpdateEstimate = (name, value) => {
    setEstimateData(prevData => ({ ...prevData, [name]: value }));
  };

  const handleUpdateAbErection = (name, value) => {
    setAbErectionData(prevData => ({ ...prevData, [name]: value }));
  };

  return (
    <>
    <Accordion
      className="tw-text-sm"
      activeKeys={activeKey}
      onChange={handleAccordionSelect}
    >
      {/* ── 1. Estimates ─────────────────────────────────────────────────── */}
      <Accordion.Item
        eventKey="0"
        header={<AccordionHeader>1. Estimates</AccordionHeader>}
      >
        <div className="tw-p-4">
          <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-4">
            <div className="tw-flex tw-items-start tw-gap-3">
              <label className="tw-w-32 tw-flex-shrink-0 tw-text-sm tw-text-slate-500 tw-pt-2">
                Request date
              </label>
              <div className="tw-flex-1">
                <DateCell
                  control={control}
                  error={errors["est_requested_date"]}
                  name="est_requested_date"
                  rules={{ required: "Request date must be required" }}
                  onChanged={adjustedDate =>
                    handleUpdateEstimate("est_requested_date", adjustedDate)
                  }
                  disabled={isScheduled}
                />
              </div>
            </div>

            {isScheduled && (
              <div className="tw-flex tw-items-start tw-gap-3">
                <label className="tw-w-32 tw-flex-shrink-0 tw-text-sm tw-text-slate-500 tw-pt-2">
                  Schedule date
                </label>
                <div className="tw-flex-1">
                  <DateCell
                    control={control}
                    error={errors["est_scheduled_date"]}
                    name="est_scheduled_date"
                    rules={{ required: "Schedule date must be required" }}
                    onChanged={adjustedDate =>
                      handleUpdateEstimate("est_scheduled_date", adjustedDate)
                    }
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </Accordion.Item>

      {/* ── 2. APD / A-B for Approval Drawings ───────────────────────────── */}
      <Accordion.Item
        eventKey="1"
        header={
          <AccordionHeader>
            2. Approval Drawings (APD), A/B for Approval Drawings
          </AccordionHeader>
        }
      >
        <div className="tw-p-4">
          <div className="tw-overflow-x-auto">
            <table className="tw-w-full tw-border-collapse">
              <thead className="tw-bg-slate-50">
                <tr>
                  <th className={`${thCls} tw-w-8`}></th>
                  <th className={thCls}>Name</th>
                  <th className={thCls}>APD request</th>
                  {isScheduled && <th className={thCls}>APD schedule</th>}
                  <th className={thCls}>A/B request</th>
                  {isScheduled && <th className={thCls}>A/B schedule</th>}
                </tr>
              </thead>
              <tbody className="tw-divide-y tw-divide-slate-100">
                {apdData &&
                  apdData.map(item => (
                    <tr
                      key={item.id}
                      className="odd:tw-bg-slate-50/50 hover:tw-bg-slate-50"
                    >
                      <td className={tdCls}>
                        <MdDelete
                          className="tw-text-red-500 tw-cursor-pointer"
                          onClick={() => handleDeleteApd(item.id)}
                        />
                      </td>
                      <td className={tdCls}>
                        <input
                          name={`apd_name_${item.id}`}
                          placeholder="Name"
                          className={inputCls}
                          {...register(`apd_name_${item.id}`, {
                            required: "Name must be required."
                          })}
                          onChange={e =>
                            handleInputChange(
                              setApdData,
                              "name",
                              item.id,
                              e.target.value,
                              `apd_name_${item.id}`
                            )
                          }
                        />
                        <FieldError error={errors[`apd_name_${item.id}`]} />
                      </td>
                      <td className={tdCls}>
                        <DateCell
                          control={control}
                          error={errors[`apd_requested_${item.id}`]}
                          name={`apd_requested_${item.id}`}
                          rules={{ required: "Request date must be required" }}
                          onChanged={adjustedDate =>
                            handleInputChange(
                              setApdData,
                              "apd_requested",
                              item.id,
                              adjustedDate
                            )
                          }
                          disabled={isScheduled}
                        />
                      </td>
                      {isScheduled && (
                        <td className={tdCls}>
                          <DateCell
                            control={control}
                            error={errors[`apd_scheduled_${item.id}`]}
                            name={`apd_scheduled_${item.id}`}
                            rules={{
                              required: "Schedule date must be required"
                            }}
                            onChanged={adjustedDate =>
                              handleInputChange(
                                setApdData,
                                "apd_scheduled",
                                item.id,
                                adjustedDate
                              )
                            }
                          />
                        </td>
                      )}
                      <td className={tdCls}>
                        <DateCell
                          control={control}
                          error={errors[`ab_erd_requested_${item.id}`]}
                          name={`ab_erd_requested_${item.id}`}
                          rules={{ required: "Request date must be required" }}
                          onChanged={adjustedDate =>
                            handleInputChange(
                              setApdData,
                              "ab_erd_requested",
                              item.id,
                              adjustedDate
                            )
                          }
                          disabled={isScheduled}
                        />
                      </td>
                      {isScheduled && (
                        <td className={tdCls}>
                          <DateCell
                            control={control}
                            error={errors[`ab_erd_scheduled_${item.id}`]}
                            name={`ab_erd_scheduled_${item.id}`}
                            rules={{
                              required: "Schedule date must be required"
                            }}
                            onChanged={adjustedDate =>
                              handleInputChange(
                                setApdData,
                                "ab_erd_scheduled",
                                item.id,
                                adjustedDate
                              )
                            }
                          />
                        </td>
                      )}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {!isScheduled && <AddRecordButton onClick={handleAddAPD} />}
        </div>
      </Accordion.Item>

      {/* ── 3. Shop / Erection Drawings ──────────────────────────────────── */}
      <Accordion.Item
        eventKey="2"
        header={<AccordionHeader>3. Shop, Erection Drawings</AccordionHeader>}
      >
        <div className="tw-p-4">
          <div className="tw-overflow-x-auto">
            <table className="tw-w-full tw-border-collapse">
              <thead className="tw-bg-slate-50">
                <tr>
                  <th className={`${thCls} tw-w-8`}></th>
                  <th className={thCls}>Name</th>
                  <th className={thCls}>Description</th>
                  <th className={thCls}>Shop request</th>
                  {isScheduled && <th className={thCls}>Shop schedule</th>}
                  <th className={thCls}>Erection request</th>
                  {isScheduled && <th className={thCls}>Erection schedule</th>}
                </tr>
              </thead>
              <tbody className="tw-divide-y tw-divide-slate-100">
                {shopData &&
                  shopData.map(item => (
                    <tr
                      key={item.id}
                      className="odd:tw-bg-slate-50/50 hover:tw-bg-slate-50"
                    >
                      <td className={tdCls}>
                        <MdDelete
                          className="tw-text-red-500 tw-cursor-pointer"
                          onClick={() => handleDeleteShop(item.id)}
                        />
                      </td>
                      <td className={tdCls}>
                        <input
                          name={`shop_name_${item.id}`}
                          placeholder="Name"
                          className={inputCls}
                          {...register(`shop_name_${item.id}`, {
                            required: "Name must be required."
                          })}
                          onChange={e =>
                            handleInputChange(
                              setShopData,
                              "name",
                              item.id,
                              e.target.value,
                              `shop_name_${item.id}`
                            )
                          }
                        />
                        <FieldError error={errors[`shop_name_${item.id}`]} />
                      </td>
                      <td className={tdCls}>
                        <input
                          name={`description_${item.id}`}
                          placeholder="Description"
                          className={inputCls}
                          {...register(`description_${item.id}`, {
                            required: "Description must be required."
                          })}
                          onChange={e =>
                            handleInputChange(
                              setShopData,
                              "description",
                              item.id,
                              e.target.value
                            )
                          }
                        />
                        <FieldError error={errors[`description_${item.id}`]} />
                      </td>
                      <td className={tdCls}>
                        {/* preserves original behavior: raw `date` (not adjusted) is stored here */}
                        <DateCell
                          control={control}
                          error={errors[`shop_requested_${item.id}`]}
                          name={`shop_requested_${item.id}`}
                          rules={{ required: "Request date must be required" }}
                          onChanged={(adjustedDate, date) =>
                            handleInputChange(
                              setShopData,
                              "shop_requested",
                              item.id,
                              date
                            )
                          }
                          disabled={isScheduled}
                        />
                      </td>
                      {isScheduled && (
                        <td className={tdCls}>
                          <DateCell
                            control={control}
                            error={errors[`shop_scheduled_${item.id}`]}
                            name={`shop_scheduled_${item.id}`}
                            rules={{
                              required: "Schedule date must be required"
                            }}
                            onChanged={adjustedDate =>
                              handleInputChange(
                                setShopData,
                                "shop_scheduled",
                                item.id,
                                adjustedDate
                              )
                            }
                          />
                        </td>
                      )}
                      <td className={tdCls}>
                        <DateCell
                          control={control}
                          error={errors[`erc_requested_${item.id}`]}
                          name={`erc_requested_${item.id}`}
                          rules={{ required: "Request date must be required" }}
                          onChanged={adjustedDate =>
                            handleInputChange(
                              setShopData,
                              "erc_requested",
                              item.id,
                              adjustedDate
                            )
                          }
                          disabled={isScheduled}
                        />
                      </td>
                      {isScheduled && (
                        <td className={tdCls}>
                          <DateCell
                            control={control}
                            error={errors[`erc_scheduled_${item.id}`]}
                            name={`erc_scheduled_${item.id}`}
                            rules={{
                              required: "Schedule date must be required"
                            }}
                            onChanged={adjustedDate =>
                              handleInputChange(
                                setShopData,
                                "erc_scheduled",
                                item.id,
                                adjustedDate
                              )
                            }
                          />
                        </td>
                      )}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {!isScheduled && (
            <div className="tw-flex tw-items-center tw-justify-between tw-mt-3">
              {copyShopSourceOptions.length > 0 ? (
                <Button
                  variant="ghost"
                  size="sm"
                  type="button"
                  onClick={() => setShowCopyShopModal(true)}
                >
                  <FaCopy size={11} />
                  <span>Copy from CO#</span>
                </Button>
              ) : (
                <span />
              )}
              <AddRecordButton onClick={handleAddShop} />
            </div>
          )}
        </div>
      </Accordion.Item>

      {/* ── 4. A/B for Erection Drawings ─────────────────────────────────── */}
      <Accordion.Item
        eventKey="3"
        header={<AccordionHeader>4. A/B for Erection Drawings</AccordionHeader>}
      >
        <div className="tw-p-4">
          <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-4">
            <div className="tw-flex tw-items-start tw-gap-3">
              <label className="tw-w-32 tw-flex-shrink-0 tw-text-sm tw-text-slate-500 tw-pt-2">
                Request date
              </label>
              <div className="tw-flex-1">
                <DateCell
                  control={control}
                  error={errors["requested_date"]}
                  name="requested_date"
                  rules={{ required: "Request date must be required" }}
                  onChanged={adjustedDate =>
                    handleUpdateAbErection("requested_date", adjustedDate)
                  }
                  disabled={isScheduled}
                />
              </div>
            </div>

            {isScheduled && (
              <div className="tw-flex tw-items-start tw-gap-3">
                <label className="tw-w-32 tw-flex-shrink-0 tw-text-sm tw-text-slate-500 tw-pt-2">
                  Schedule date
                </label>
                <div className="tw-flex-1">
                  <DateCell
                    control={control}
                    error={errors["scheduled_date"]}
                    name="scheduled_date"
                    rules={{ required: "Schedule date must be required" }}
                    onChanged={adjustedDate =>
                      handleUpdateAbErection("scheduled_date", adjustedDate)
                    }
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </Accordion.Item>

      {/* ── 5. Other Drawings ────────────────────────────────────────────── */}
      <Accordion.Item
        eventKey="4"
        header={<AccordionHeader>5. Other Drawings</AccordionHeader>}
      >
        <div className="tw-p-4">
          <div className="tw-overflow-x-auto">
            <table className="tw-w-full tw-border-collapse">
              <thead className="tw-bg-slate-50">
                <tr>
                  <th className={`${thCls} tw-w-8`}></th>
                  <th className={thCls}>Name</th>
                  <th className={thCls}>Request date</th>
                  {isScheduled && <th className={thCls}>Schedule date</th>}
                </tr>
              </thead>
              <tbody className="tw-divide-y tw-divide-slate-100">
                {otherData &&
                  otherData.map(item => (
                    <tr
                      key={item.id}
                      className="odd:tw-bg-slate-50/50 hover:tw-bg-slate-50"
                    >
                      <td className={tdCls}>
                        <MdDelete
                          className="tw-text-red-500 tw-cursor-pointer"
                          onClick={() => handleDeleteOther(item.id)}
                        />
                      </td>
                      <td className={tdCls}>
                        <input
                          name={`other_name_${item.id}`}
                          placeholder="Name"
                          className={inputCls}
                          {...register(`other_name_${item.id}`, {
                            required: "Name must be required."
                          })}
                          onChange={e =>
                            handleInputChange(
                              setOtherData,
                              "name",
                              item.id,
                              e.target.value,
                              `other_name_${item.id}`
                            )
                          }
                        />
                        <FieldError error={errors[`other_name_${item.id}`]} />
                      </td>
                      <td className={tdCls}>
                        <DateCell
                          control={control}
                          error={errors[`requested_date_${item.id}`]}
                          name={`requested_date_${item.id}`}
                          rules={{ required: "Request date must be required" }}
                          onChanged={adjustedDate =>
                            handleInputChange(
                              setOtherData,
                              "requested_date",
                              item.id,
                              adjustedDate
                            )
                          }
                          disabled={isScheduled}
                        />
                      </td>
                      {isScheduled && (
                        <td className={tdCls}>
                          <DateCell
                            control={control}
                            error={errors[`scheduled_date_${item.id}`]}
                            name={`scheduled_date_${item.id}`}
                            rules={{
                              required: "Schedule date must be required"
                            }}
                            onChanged={adjustedDate =>
                              handleInputChange(
                                setOtherData,
                                "scheduled_date",
                                item.id,
                                adjustedDate
                              )
                            }
                          />
                        </td>
                      )}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {!isScheduled && <AddRecordButton onClick={handleAddOther} />}
        </div>
      </Accordion.Item>
    </Accordion>

    <Modal
      show={showCopyShopModal}
      onHide={() => setShowCopyShopModal(false)}
      title="Copy Shop drawings from another CO#"
      size="md"
      backdropClick={false}
      footer={
        <div className="tw-flex tw-gap-2">
          <Button variant="ghost" size="sm" onClick={() => setShowCopyShopModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            leftIcon={FaCopy}
            loading={copyingShop}
            disabled={!copyShopSourceId}
            onClick={handleCopyShop}
          >
            Copy
          </Button>
        </div>
      }
    >
      <FormRow label="Copy from">
        <Select
          classNamePrefix="react-select"
          options={copyShopSourceOptions}
          value={copyShopSourceOptions.find(o => o.value === copyShopSourceId) || null}
          onChange={o => setCopyShopSourceId(o ? o.value : "")}
          placeholder="Select a change order…"
          isClearable
          formatOptionLabel={o => (
            <span className="tw-flex tw-items-center tw-gap-2">
              <FaCodeBranch className="tw-text-blue-500" size={12} />
              {o.label}
            </span>
          )}
          {...menuPortalProps}
        />
      </FormRow>

      <Alert type="info" className="tw-mt-3">
        <div className="tw-mb-1.5">
          Copied into <strong>CO#{changeOrder?.co}</strong>:
        </div>
        <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-mb-2">
          {["Name", "Description", "Shop request", "Erection request"].map(f => (
            <span
              key={f}
              className="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-xl tw-bg-white tw-border tw-border-solid tw-border-blue-200 tw-text-blue-700 tw-text-xs tw-font-medium"
            >
              {f}
            </span>
          ))}
        </div>
        <div className="tw-text-xs tw-text-slate-500">
          Rows are added to the list below but <strong>not saved</strong> until you send the
          request. Phases whose name already exists here are skipped.
        </div>
      </Alert>
    </Modal>
    </>
  );
};

RequestScheduleForm.propTypes = {
  changeOrder: PropTypes.object.isRequired,
  estimateData: PropTypes.object.isRequired,
  setEstimateData: PropTypes.func.isRequired,
  abErectionData: PropTypes.object.isRequired,
  setAbErectionData: PropTypes.func.isRequired,
  apdData: PropTypes.object.isRequired,
  setApdData: PropTypes.func.isRequired,
  shopData: PropTypes.object.isRequired,
  setShopData: PropTypes.func.isRequired,
  otherData: PropTypes.object.isRequired,
  setOtherData: PropTypes.func.isRequired,
  isScheduled: PropTypes.bool.isRequired
};

export default RequestScheduleForm;
